import { ArrowRight, Edit } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useJourney } from '../../contexts/JourneyContext';
import api from '../../services/api';
import { ResumeData } from '../../types';
import './ReviewData.css';

const ReviewData: React.FC = () => {
  const navigate = useNavigate();
  const { currentJourney } = useJourney();
  const [data, setData] = useState<ResumeData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      if (!currentJourney) {
        navigate('/dashboard');
        return;
      }

      try {
        // Fetch fresh data directly from API to avoid context timing issues
        console.log('Fetching journey data from API...');
        const journey = await api.getJourney(currentJourney.id);
        console.log('Journey from API:', journey);
        console.log('CollectedData:', journey.collectedData);
        
        if (journey.collectedData) {
          setData(journey.collectedData);
        } else {
          console.error('No collectedData in journey!');
        }
      } catch (error) {
        console.error('Failed to load journey:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // We only care about changes to the journey id here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentJourney?.id, navigate]);

  const handleProceedToTemplates = () => {
    navigate('/journey/templates');
  };

  const handleEditStage = async (stageNumber: number) => {
    if (!currentJourney) return;
    
    try {
      console.log('[Edit] Editing stage:', stageNumber);
      
      // Route based on stage type:
      // Stages 0-2: Form-based (Personal, Education, Employment) - navigate to forms
      // Stages 3-4: Voice-based (Projects, Skills) - navigate to conversational flow
      
      if (stageNumber === 0) {
        // Personal Info - navigate to form
        navigate('/journey/forms/personal');
      } else if (stageNumber === 1) {
        // Education - navigate to form
        navigate('/journey/forms/education');
      } else if (stageNumber === 2) {
        // Employment - navigate to form
        navigate('/journey/forms/employment');
      } else if (stageNumber === 3 || stageNumber === 4) {
        // Projects or Skills - navigate to conversational flow with edit mode
        await api.updateJourneyStage(currentJourney.id, stageNumber);
        await new Promise(resolve => setTimeout(resolve, 100));
        navigate('/journey/stages', { 
          state: { 
            editMode: true, 
            editStage: stageNumber,
            journeyId: currentJourney.id 
          } 
        });
      }
    } catch (error) {
      console.error('[Edit] Failed to edit stage:', error);
      alert('Failed to go back to edit. Please try again.');
    }
  };

  if (loading) {
    return <div className="loading">Loading your information...</div>;
  }

  if (!data) {
    return <div className="loading">Processing your information...</div>;
  }

  if (!data.personal || !data.personal.name) {
    console.error('Personal data missing:', data.personal);
    // If no data was extracted, show error with option to go back
    return (
      <div className="review-container">
        <div className="review-header">
          <h1>Oops! No Data Found</h1>
          <p>We couldn't extract your information from the conversation. This might be because:</p>
          <ul style={{ textAlign: 'left', maxWidth: '600px', margin: '20px auto' }}>
            <li>The conversation didn't contain enough information</li>
            <li>There was an error processing your responses</li>
            <li>The AI extraction service failed</li>
          </ul>
          <div style={{ marginTop: '20px', padding: '15px', background: '#fff3cd', borderRadius: '8px' }}>
            <strong>Debug Info:</strong>
            <pre style={{ fontSize: '0.9rem', textAlign: 'left', overflow: 'auto', maxHeight: '200px' }}>
              {JSON.stringify(currentJourney?.collectedData, null, 2)}
            </pre>
          </div>
        </div>
        <div className="review-actions" style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
          <button 
            onClick={() => navigate('/journey/stages')}
            className="btn-secondary"
          >
            ← Go Back to Conversation
          </button>
          <button 
            onClick={() => navigate('/dashboard')}
            className="btn-primary"
          >
            Start Over
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="review-container">
      <div className="review-header">
        <h1>Review Your Information</h1>
        <p>Please review the information we've collected. You can edit this later.</p>
      </div>

      <div className="review-content">
        {/* Personal Information */}
        <section className="review-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>Personal Information</h2>
            <button 
              onClick={() => handleEditStage(0)} 
              className="btn-edit"
              title="Edit personal information"
            >
              <Edit size={14} />
              Edit
            </button>
          </div>
          <div className="info-grid">
            <div className="info-item"><strong>Name:</strong> {data.personal?.name || 'Not provided'}</div>
            <div className="info-item"><strong>Email:</strong> {data.personal?.email || 'Not provided'}</div>
            <div className="info-item"><strong>Phone:</strong> {data.personal?.phone || 'Not provided'}</div>
            <div className="info-item"><strong>Location:</strong> {data.personal?.location || 'Not provided'}</div>
            <div className="info-item"><strong>Current Role:</strong> {data.personal?.currentDesignation || 'Not provided'}</div>
            <div className="info-item"><strong>Target Position:</strong> {data.personal?.targetPosition || 'Not provided'}</div>
            {data.personal?.linkedin && <div className="info-item"><strong>LinkedIn:</strong> {data.personal.linkedin}</div>}
            {data.personal?.github && <div className="info-item"><strong>GitHub:</strong> {data.personal.github}</div>}
            {data.personal?.portfolio && <div className="info-item"><strong>Portfolio:</strong> {data.personal.portfolio}</div>}
          </div>
        </section>

        {/* Professional Summary */}
        {data.summary && (
          <section className="review-section">
            <h2>Professional Summary</h2>
            <p className="summary-text">{data.summary}</p>
          </section>
        )}

        {/* Education */}
        {data.education && data.education.length > 0 && (
          <section className="review-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2>Education</h2>
              <button 
                onClick={() => handleEditStage(1)} 
                className="btn-edit"
                title="Edit education"
              >
                <Edit size={14} />
              Edit
              </button>
            </div>
            {[...data.education]
              .sort((a, b) => {
                // Sort by end date (or start date if no end date) in descending order (latest first)
                const dateA = a.endDate || a.startDate || '';
                const dateB = b.endDate || b.startDate || '';
                return dateB.localeCompare(dateA);
              })
              .map((edu, index) => (
              <div key={index} className="item-card">
                <h3>{edu.degree} in {edu.field}</h3>
                <p><strong>{edu.institution}</strong> {edu.location && `• ${edu.location}`}</p>
                <p>{edu.startDate} - {edu.endDate || 'Present'}</p>
                {edu.gpa && <p>GPA: {edu.gpa}</p>}
                {edu.honors && (
                  <p>{typeof edu.honors === 'string' ? edu.honors : edu.honors.join(', ')}</p>
                )}
              </div>
            ))}
          </section>
        )}

        {/* Work Experience */}
        {data.employment && data.employment.length > 0 && (
          <section className="review-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2>Work Experience</h2>
              <button 
                onClick={() => handleEditStage(2)} 
                className="btn-edit"
                title="Edit work experience"
              >
                <Edit size={14} />
              Edit
              </button>
            </div>
            {[...data.employment]
              .sort((a, b) => {
                // Sort by end date (or current, or start date) in descending order (latest first)
                const dateA = a.isCurrent ? '9999-12-31' : (a.endDate || a.startDate || '');
                const dateB = b.isCurrent ? '9999-12-31' : (b.endDate || b.startDate || '');
                return dateB.localeCompare(dateA);
              })
              .map((job, index) => (
              <div key={index} className="item-card">
                <h3>{job.position}</h3>
                <p><strong>{job.company}</strong> {job.location && `• ${job.location}`}</p>
                <p>{job.startDate} - {job.isCurrent ? 'Present' : job.endDate}</p>
                <p>{job.description}</p>
                {job.achievements && job.achievements.length > 0 && (
                  <div>
                    <strong>Key Achievements:</strong>
                    <ul>
                      {job.achievements.map((achievement, i) => <li key={i}>{achievement}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </section>
        )}

        {/* Projects */}
        {data.projects && data.projects.length > 0 && (
          <section className="review-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2>Projects</h2>
              <button 
                onClick={() => handleEditStage(3)} 
                className="btn-edit"
                title="Edit projects"
              >
                <Edit size={14} />
              Edit
              </button>
            </div>
            {data.projects.map((project, index) => (
              <div key={index} className="item-card">
                <h3>{project.name}</h3>
                <p>{project.description}</p>
                {project.technologies && project.technologies.length > 0 && (
                  <p><strong>Technologies:</strong> {project.technologies.join(', ')}</p>
                )}
                {project.impact && <p><strong>Impact:</strong> {project.impact}</p>}
                {project.highlights && project.highlights.length > 0 && (
                  <ul>
                    {project.highlights.map((highlight, i) => <li key={i}>{highlight}</li>)}
                  </ul>
                )}
                {project.url && <p><a href={project.url} target="_blank" rel="noopener noreferrer">View Project</a></p>}
              </div>
            ))}
          </section>
        )}

        {/* Skills */}
        {data.skills && data.skills.length > 0 && (
          <section className="review-section">
            <h2>Skills</h2>
            <div className="skills-list">
              {data.skills.map((skill, index) => (
                <span key={index} className="skill-tag">{skill}</span>
              ))}
            </div>
          </section>
        )}

        {/* Certifications */}
        {data.certifications && data.certifications.length > 0 && (
          <section className="review-section">
            <h2>Certifications</h2>
            {data.certifications.map((cert, index) => (
              <div key={index} className="item-card">
                <h4>{cert.name}</h4>
                <p><strong>{cert.issuer}</strong> • Issued {cert.issueDate}</p>
                {cert.expiryDate && <p>Expires: {cert.expiryDate}</p>}
                {cert.credentialId && <p>Credential ID: {cert.credentialId}</p>}
                {cert.url && <p><a href={cert.url} target="_blank" rel="noopener noreferrer">View Credential</a></p>}
              </div>
            ))}
          </section>
        )}

        {/* Awards */}
        {data.awards && data.awards.length > 0 && (
          <section className="review-section">
            <h2>Awards & Recognition</h2>
            {data.awards.map((award, index) => (
              <div key={index} className="item-card">
                <h4>{award.title}</h4>
                <p><strong>{award.issuer}</strong> • {award.date}</p>
                {award.description && <p>{award.description}</p>}
              </div>
            ))}
          </section>
        )}

        {/* Publications */}
        {data.publications && data.publications.length > 0 && (
          <section className="review-section">
            <h2>Publications</h2>
            {data.publications.map((pub, index) => (
              <div key={index} className="item-card">
                <h4>{pub.title}</h4>
                <p><strong>{pub.publisher}</strong> • {pub.date}</p>
                {pub.description && <p>{pub.description}</p>}
                {pub.url && <p><a href={pub.url} target="_blank" rel="noopener noreferrer">View Publication</a></p>}
              </div>
            ))}
          </section>
        )}

        {/* Volunteer Work */}
        {data.volunteer && data.volunteer.length > 0 && (
          <section className="review-section">
            <h2>Volunteer Experience</h2>
            {data.volunteer.map((vol, index) => (
              <div key={index} className="item-card">
                <h4>{vol.role}</h4>
                <p><strong>{vol.organization}</strong></p>
                <p>{vol.startDate} - {vol.endDate || 'Present'}</p>
                <p>{vol.description}</p>
              </div>
            ))}
          </section>
        )}
      </div>

      <div className="review-actions">
        <button 
          onClick={handleProceedToTemplates}
          className="btn-primary btn-proceed"
        >
          Select Template
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default ReviewData;

